import { Preferences } from '@capacitor/preferences';
import { isNativeApp } from '../utils/platformUtils';

// --- CONFIGURAÇÃO INDEXEDDB (Fallback resiliente para iOS PWA) ---
const DB_NAME = 'MeuPainelDB';
const DB_VERSION = 2; // Incrementado para garantir criação do object store
const STORE_NAME = 'app_data';
const syncCache = new Map();

/**
 * Abre o banco de dados IndexedDB
 */
const openDB = () => {
    return new Promise((resolve, reject) => {
        try {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    db.createObjectStore(STORE_NAME);
                    console.log(`[Storage IDB] Object store '${STORE_NAME}' criado.`);
                }
            };

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => {
                console.error('[Storage IDB] Erro ao abrir banco:', request.error);
                reject(request.error);
            };
        } catch (err) {
            console.error('[Storage IDB] Exceção ao abrir banco:', err);
            reject(err);
        }
    });
};

/**
 * Salva no IndexedDB
 */
const setIDBItem = async (key, value) => {
    try {
        const db = await openDB();
        if (!db.objectStoreNames.contains(STORE_NAME)) {
            throw new Error(`Store '${STORE_NAME}' não encontrado no banco.`);
        }

        return await new Promise((resolve, reject) => {
            try {
                const transaction = db.transaction(STORE_NAME, 'readwrite');
                const store = transaction.objectStore(STORE_NAME);
                const request = store.put(value, key);

                request.onsuccess = () => resolve(true);
                request.onerror = () => reject(request.error);
            } catch (err) {
                reject(err);
            }
        });
    } catch (e) {
        console.warn('[Storage IDB] Erro ao salvar:', e);
        return false;
    }
};

/**
 * Lê do IndexedDB
 */
const getIDBItem = async (key) => {
    try {
        const db = await openDB();
        if (!db.objectStoreNames.contains(STORE_NAME)) {
            return null; // Silencioso se não existir o store
        }

        return await new Promise((resolve, reject) => {
            try {
                const transaction = db.transaction(STORE_NAME, 'readonly');
                const store = transaction.objectStore(STORE_NAME);
                const request = store.get(key);

                request.onsuccess = () => resolve(request.result);
                request.onerror = () => reject(request.error);
            } catch (err) {
                reject(err);
            }
        });
    } catch (e) {
        console.warn('[Storage IDB] Erro ao ler:', e);
        return null;
    }
};

/**
 * Salva um item no storage apropriado + IDB como backup
 * @param {string} key - Chave do item
 * @param {any} value - Valor a ser salvo (será serializado com JSON.stringify no LS)
 */
export const setItem = async (key, value) => {
    try {
        // 1. Salva no storage principal (Preferences ou LocalStorage)
        const stringValue = JSON.stringify(value);
        if (isNativeApp()) {
            await Preferences.set({ key, value: stringValue });
        } else {
            window.localStorage.setItem(key, stringValue);
        }
        syncCache.set(key, value);

        // 2. Backup Silencioso no IndexedDB (Muito mais estável no iOS PWA)
        if (!isNativeApp()) {
            setIDBItem(key, value); // Background backup (non-blocking)
        }

        return true;
    } catch (error) {
        console.error(`[Storage] Erro ao salvar ${key}:`, error);
        if (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
            alert('O armazenamento do seu navegador está cheio. Libere espaço para salvar novos registros.');
        }
        return false;
    }
};

/**
 * Obtém um item do storage apropriado com fallback para IndexedDB
 * @param {string} key - Chave do item
 * @returns {Promise<any>} - Valor parseado ou null se não existir
 */
export const getItem = async (key) => {
    try {
        let value = null;

        // 1. Tenta storage principal
        if (isNativeApp()) {
            const { value: res } = await Preferences.get({ key });
            if (res === null || res === "undefined" || res === "[object Object]") {
                if (res !== null) await Preferences.remove({ key });
                value = null;
            } else {
                try {
                    value = JSON.parse(res);
                } catch (e) {
                    value = res; // Fallback for plain strings
                }
            }
        } else {
            const item = window.localStorage.getItem(key);
            if (item === null || item === "undefined" || item === "[object Object]") {
                if (item !== null) window.localStorage.removeItem(key);
                value = null;
            } else {
                try {
                    value = JSON.parse(item);
                } catch (e) {
                    value = item; // Fallback for plain strings
                }
            }
        }

        // 2. Se falhar (ex: purga do iOS no localStorage), tenta IndexedDB
        if (value === null && !isNativeApp()) {
            const idbValue = await getIDBItem(key);
            if (idbValue !== null) {
                console.log(`[Storage] Recuperando ${key} do IndexedDB Backup após falha no localStorage.`);
                // Restaura o localStorage se possível para manter consistência
                try { window.localStorage.setItem(key, JSON.stringify(idbValue)); } catch (e) { }
                return idbValue;
            }
        }

        // 3. Update cache
        if (value !== null) syncCache.set(key, value);
        return value;
    } catch (error) {
        console.error(`Erro ao ler ${key} do storage:`, error);
        return null;
    }
};

/**
 * Inicializa o cache síncrono para acesso rápido (Crucial para Android)
 */
export const initCache = async () => {
    try {
        console.log('[Storage] Inicializando cache...');
        const allKeys = await keys();
        const promises = allKeys.map(async (key) => {
            const val = await getItem(key);
            if (val !== null) syncCache.set(key, val);
        });
        await Promise.all(promises);
        console.log(`[Storage] Cache pronto: ${syncCache.size} itens carregados.`);
        return true;
    } catch (e) {
        console.error('[Storage] Erro ao inicializar cache:', e);
        return false;
    }
};

/**
 * Obtém um item de forma síncrona
 */
export const getItemSync = (key, defaultValue = null) => {
    if (syncCache.has(key)) return syncCache.get(key);
    if (isNativeApp()) return defaultValue;

    try {
        const item = window.localStorage.getItem(key);
        if (item === null || item === "undefined" || item === "[object Object]") {
            if (item !== null) window.localStorage.removeItem(key);
            return defaultValue;
        }
        let val;
        try {
            val = JSON.parse(item);
        } catch (e) {
            val = item;
        }
        syncCache.set(key, val);
        return val;
    } catch (error) {
        return defaultValue;
    }
};

/**
 * Salva um item de forma síncrona no cache e no localStorage
 * Se nativo, sincroniza com Preferences em background
 */
export const setItemSync = (key, value) => {
    try {
        syncCache.set(key, value);
        const stringValue = JSON.stringify(value);
        if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.setItem(key, stringValue);
        }
        if (isNativeApp()) {
            Preferences.set({ key, value: stringValue }).catch(() => {});
        } else {
            setIDBItem(key, value);
        }
        return true;
    } catch (error) {
        console.error(`[Storage] Erro no setItemSync ${key}:`, error);
        return false;
    }
};

/**
 * Remove um item de forma síncrona do cache e localStorage
 */
export const removeItemSync = (key) => {
    try {
        syncCache.delete(key);
        if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.removeItem(key);
        }
        if (isNativeApp()) {
            Preferences.remove({ key }).catch(() => {});
        }
        return true;
    } catch (error) {
        console.error(`[Storage] Erro no removeItemSync ${key}:`, error);
        return false;
    }
};

/**
 * Remove um item do storage e IDB
 * @param {string} key - Chave do item
 */
export const removeItem = async (key) => {
    syncCache.delete(key);
    if (isNativeApp()) {
        await Preferences.remove({ key });
    } else {
        window.localStorage.removeItem(key);
        // Remove do IDB também
        try {
            const db = await openDB();
            if (db.objectStoreNames.contains(STORE_NAME)) {
                const transaction = db.transaction(STORE_NAME, 'readwrite');
                transaction.objectStore(STORE_NAME).delete(key);
            }
        } catch (e) { }
    }
};

/**
 * Limpa todo o storage
 */
export const clear = async () => {
    syncCache.clear();
    if (isNativeApp()) {
        await Preferences.clear();
    } else {
        window.localStorage.clear();
        // Não limpamos o IDB por segurança (ou limpamos se for reset total?)
        // Vamos manter o IDB limpo também para clear() ser consistente
        try {
            const db = await openDB();
            if (db.objectStoreNames.contains(STORE_NAME)) {
                const transaction = db.transaction(STORE_NAME, 'readwrite');
                transaction.objectStore(STORE_NAME).clear();
            }
        } catch (e) { }
    }
};

/**
 * Obtém todas as chaves do storage
 */
export const keys = async () => {
    if (isNativeApp()) {
        const { keys } = await Preferences.keys();
        return keys;
    } else {
        return Object.keys(window.localStorage);
    }
};

export const isNativeStorage = () => isNativeApp();

export const migrateFromLocalStorage = async () => {
    if (!isNativeApp()) return;
    const migrationKey = 'rotaDoLucro_migrated_to_native';
    const { value: alreadyMigrated } = await Preferences.get({ key: migrationKey });
    if (alreadyMigrated) return;

    const keysToMigrate = [
        'rotaDoLucroData', 'rotaDoLucroVehicles', 'rotaDoLucroProfile',
        'rotaDoLucroTheme', 'rotaDoLucroThemeSchedule', 'rotaDoLucroKm', 'rotaDoLucroCustomCategories',
        'rotaDoLucroFixedExpenses', 'rotaDoLucroAgenda', 'rotaDoLucroDailyManualStats',
        'rotaDoLucroGoal', 'rotaDoLucroWorkDays', 'rotaDoLucroProfitTarget'
    ];

    for (const key of keysToMigrate) {
        try {
            const value = window.localStorage.getItem(key);
            if (value) await Preferences.set({ key, value });
        } catch (e) { }
    }
    await Preferences.set({ key: migrationKey, value: 'true' });
};

export const checkAppVersion = async (currentVersion) => {
    const versionKey = 'rotaDoLucro_app_version';
    let storedVersion = isNativeApp()
        ? (await Preferences.get({ key: versionKey })).value
        : window.localStorage.getItem(versionKey);

    if (storedVersion !== currentVersion) {
        await setItem(versionKey, currentVersion);
    }
};

export default {
    setItem,
    setItemSync,
    getItem,
    getItemSync,
    initCache,
    removeItem,
    removeItemSync,
    clear,
    keys,
    isNativeStorage,
    migrateFromLocalStorage,
    checkAppVersion
};
